// frontend/src/utils/photoUploadQueue.js
// =====================================================================
// [PHOTO_QUEUE_V1] 매물 사진 백그라운드 업로드 관리자 (화면 쪽)
//
// ■ 역할
//   - PhotoGallery 가 넘긴 사진을 "1장씩" 서버에 전달
//       POST /api/properties/:id/photos/queue   (backend services/photoQueue.js)
//     서버가 받자마자 응답하므로 1장에 수십 ms. 구글 업로드는 서버가 뒤에서 처리.
//   - 서버 처리 진행상황을 3초마다 확인
//       GET  /api/properties/:id/photos/jobs?ids=...
//   - 화면 우측 하단에 진행표시 (document.body 에 직접 그림 → App.js 수정 불필요)
//   - 끝나면 이벤트 발송
//       photo:queue-progress  {estateNo, done}   → 열려 있는 갤러리가 목록 새로고침
//       photo:queue-done      {estateNo, ...}    → 갤러리 최종 갱신
//       photo:cover / photo:changed             → 매물 목록 썸네일 갱신 (기존 이벤트 그대로)
//
// ■ 왜 React 컴포넌트가 아니라 모듈인가
//   PhotoGallery 는 매물창을 닫으면 사라진다(unmount). 전송 반복문이 그 안에 있으면
//   창을 닫는 순간 멈춘다. 모듈 변수는 페이지(탭)가 살아 있는 동안 유지된다.
//
// ■ 한계
//   - "서버 전달 전" 사진은 탭을 닫으면 사라짐 → 그 동안만 닫기 경고(beforeunload)
//   - 서버 전달이 끝난 사진은 탭을 닫거나 새로고침해도 서버가 계속 처리
//   - 새로고침하면 진행표시만 사라진다 (작업은 계속됨)
//
// ■ 토큰: 기존 photoApi.js 와 같이 일반 fetch 사용 (전역 fetch 래퍼가 토큰을 붙임)
// =====================================================================
import { listPhotos } from "../api/photoApi";

export const MAX_PER_SELECT = 50;   // 한 번에 고를 수 있는 장수 (매물당 50장은 서버가 검사)
const POLL_MS = 3000;
const AUTO_HIDE_MS = 8000;          // 실패 없이 끝난 묶음은 8초 뒤 표시 닫기

const ACTIVE = ["waiting", "sending", "queued", "processing"];
const tasks = [];                   // 묶음(한 번 선택) 단위
let taskSeq = 0;
let sending = false;
let pollTimer = null;
let box = null;

const sleep = (ms) => new Promise((ok) => setTimeout(ok, ms));

// 대표사진 표시 주소 — PhotoGallery.js proxyThumb() 와 같은 규칙
//   (PhotoGallery 가 이 파일을 import 하므로 거꾸로 import 하면 순환참조가 되어 복사해 둠)
function coverUrl(p = {}, sz = "w600") {
  const url = p.thumb_url || p.url || "";
  if (url.includes("drive.google.com") && url.includes("sz=")) return url.replace(/sz=[^&]+/, `sz=${sz}`);
  const m = url.match(/[?&]id=([A-Za-z0-9_\-]+)/) || url.match(/\/file\/d\/([A-Za-z0-9_\-]+)/);
  const id = p.thumb_file_id || (m && m[1]);
  return id ? `/api/photo-storage/${id}/preview?sz=${sz}` : (url || "/images/no-photo.png");
}

// ---------------------------------------------------------------------
// 남은 자리 확인 (매물당 50장). 실패하면 null → 서버가 어차피 409 로 막아줌
//   아직 서버에 못 보낸 이 탭의 대기분도 빼서 계산
// ---------------------------------------------------------------------
export async function getPhotoCapacity(propertyId) {
  try {
    const r = await fetch(`/api/properties/${propertyId}/photos/jobs`, { cache: "no-store" });
    if (!r.ok) return null;
    const j = await r.json();
    const localWaiting = tasks
      .filter((t) => String(t.propertyId) === String(propertyId))
      .reduce((n, t) => n + t.items.filter((i) => i.st === "waiting" || i.st === "sending").length, 0);
    return { ...j, remaining: Math.max(0, Number(j.remaining || 0) - localWaiting) };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------
// 사진 묶음 등록 — files 는 이미 이름을 바꾼 File 배열 (PhotoGallery executeUpload)
// ---------------------------------------------------------------------
export function enqueuePhotos(propertyId, files, { label } = {}) {
  const list = Array.from(files || []);
  if (!list.length) return null;
  const task = {
    id: ++taskSeq,
    propertyId,                         // 기존 이벤트와 같은 값(타입) 그대로 전달하기 위해 원본 유지
    label: label || `매물 ${propertyId}`,
    items: list.map((f) => ({ file: f, name: f.name, st: "waiting", jobId: null, err: "" })),
    limitMsg: "",
    lastDone: 0,
    finished: false,
    dismissed: false,
  };
  tasks.push(task);
  render();
  pump();
  return task.id;
}

// ---------------------------------------------------------------------
// 전송: 모든 묶음을 통틀어 1장씩 (도착 순서 = 선택 순서 → 서버가 그 순서로 DB 반영)
// ---------------------------------------------------------------------
async function pump() {
  if (sending) return;
  sending = true;
  try {
    for (;;) {
      const t = tasks.find((x) => x.items.some((i) => i.st === "waiting"));
      if (!t) break;
      const it = t.items.find((i) => i.st === "waiting");
      it.st = "sending";
      render();

      try {
        const fd = new FormData();
        fd.append("file", it.file, it.name);
        const r = await fetch(`/api/properties/${t.propertyId}/photos/queue`, { method: "POST", body: fd });
        const j = await r.json().catch(() => ({}));

        if (r.ok && j.ok) {
          it.st = "queued";
          it.jobId = j.job_id;
          it.file = null;                  // 서버에 넘겼으니 브라우저 메모리에서 놓아줌
        } else if (r.status === 409) {
          // 매물당 최대 장수 초과 → 이 묶음의 남은 사진은 보내지 않음
          const left = t.items.filter((i) => i === it || i.st === "waiting");
          left.forEach((i) => { i.st = "skipped"; i.file = null; });
          t.limitMsg = `매물당 최대 ${j.max || 50}장 — ${left.length}장은 올리지 못했습니다`;
        } else {
          it.st = "failed";
          it.err = j.error || j.message || `HTTP ${r.status}`;
          it.file = null;
        }
      } catch (e) {
        // 네트워크 끊김 등 → 2번까지 다시 시도
        it.retry = (it.retry || 0) + 1;
        if (it.retry < 3) {
          it.st = "waiting";
          await sleep(2000 * it.retry);
        } else {
          it.st = "failed";
          it.err = String(e?.message || e);
          it.file = null;
        }
      }
      render();
      ensurePoll();
    }
  } finally {
    sending = false;
  }
}

// ---------------------------------------------------------------------
// 서버 처리 상황 확인
// ---------------------------------------------------------------------
function ensurePoll() {
  if (!pollTimer) pollTimer = setTimeout(poll, POLL_MS);
}

async function poll() {
  pollTimer = null;
  for (const t of tasks.filter((x) => !x.finished)) {
    const ids = t.items.filter((i) => i.jobId && (i.st === "queued" || i.st === "processing")).map((i) => i.jobId);
    for (let k = 0; k < ids.length; k += 100) {
      try {
        const r = await fetch(`/api/properties/${t.propertyId}/photos/jobs?ids=${ids.slice(k, k + 100).join(",")}`, { cache: "no-store" });
        if (!r.ok) continue;
        const j = await r.json();
        for (const row of j.jobs || []) {
          const it = t.items.find((i) => i.jobId === row.id);
          if (!it) continue;
          if (row.status === "done") it.st = "done";
          else if (row.status === "failed") { it.st = "failed"; it.err = row.error || "처리 실패"; }
          else it.st = "processing";      // pending / processing / uploaded(순서 대기)
        }
      } catch { /* 다음 확인 때 다시 */ }
    }

    const done = t.items.filter((i) => i.st === "done").length;
    if (done !== t.lastDone) {
      t.lastDone = done;
      window.dispatchEvent(new CustomEvent("photo:queue-progress", { detail: { estateNo: t.propertyId, done } }));
    }

    const stillActive = t.items.some((i) => ACTIVE.includes(i.st));
    if (!stillActive && !t.finished) {
      t.finished = true;
      await notifyDone(t);
    }
  }
  render();
  if (tasks.some((x) => !x.finished)) ensurePoll();
}

// 묶음 완료 → 기존 PhotoGallery 가 업로드 후 하던 화면 갱신을 대신 수행
//   (서버가 이미 대표사진을 정리했으므로 여기선 화면 반영만)
async function notifyDone(t) {
  const done = t.items.filter((i) => i.st === "done").length;
  const failed = t.items.filter((i) => i.st === "failed").length;
  const skipped = t.items.filter((i) => i.st === "skipped").length;

  if (done > 0) {
    try {
      const ps = await listPhotos(t.propertyId);
      if (ps[0]) {
        const u = coverUrl(ps[0], "w600");
        await new Promise((ok) => { const im = new Image(); im.onload = ok; im.onerror = ok; im.src = u; });
        window.dispatchEvent(new CustomEvent("photo:cover", { detail: { estateNo: t.propertyId, url: u } }));
      }
    } catch { /* 목록 갱신 실패는 무시 — 새로고침하면 보임 */ }
    window.dispatchEvent(new Event("photo:changed"));
  }
  window.dispatchEvent(new CustomEvent("photo:queue-done", { detail: { estateNo: t.propertyId, done, failed, skipped } }));

  if (failed === 0 && skipped === 0) {
    setTimeout(() => { t.dismissed = true; render(); }, AUTO_HIDE_MS);
  }
}

// ---------------------------------------------------------------------
// 진행표시 (우측 하단). innerHTML 을 쓰지 않음 — 주소·건물명에 특수문자가 있어도 안전
// ---------------------------------------------------------------------
function el(tag, style, text) {
  const e = document.createElement(tag);
  if (style) Object.assign(e.style, style);
  if (text != null) e.textContent = text;
  return e;
}

function render() {
  if (typeof document === "undefined") return;

  // 끝났고 닫은 묶음은 목록에서 제거 (메모리 정리)
  for (let k = tasks.length - 1; k >= 0; k--) {
    if (tasks[k].finished && tasks[k].dismissed) tasks.splice(k, 1);
  }
  const visible = tasks.filter((t) => !t.dismissed);
  if (!visible.length) {
    if (box) { box.remove(); box = null; }
    return;
  }

  if (!box) {
    box = el("div", {
      position: "fixed", right: "16px", bottom: "16px",
      zIndex: "2147482000",                // 사진 슬라이드(2147483000)보다는 아래
      background: "#fff", border: "1px solid #cfd8e3", borderRadius: "8px",
      boxShadow: "0 4px 16px rgba(0,0,0,.18)", padding: "8px 10px",
      font: "12px/1.5 sans-serif", color: "#222", width: "280px", maxHeight: "50vh", overflowY: "auto",
    });
    box.id = "photo-upload-queue";
    box.addEventListener("click", (e) => {
      const b = e.target.closest("[data-hide]");
      if (!b) return;
      const t = tasks.find((x) => x.id === Number(b.dataset.hide));
      if (t) { t.dismissed = true; render(); }   // 표시만 닫음 — 작업은 계속
    });
    document.body.appendChild(box);
  }

  box.replaceChildren();
  for (const t of visible) {
    const n = t.items.length;
    const sent = t.items.filter((i) => i.jobId).length;
    const done = t.items.filter((i) => i.st === "done").length;
    const failed = t.items.filter((i) => i.st === "failed").length;

    const row = el("div", { padding: "4px 0", borderTop: box.childNodes.length ? "1px solid #eee" : "none" });

    const head = el("div", { display: "flex", justifyContent: "space-between", gap: "6px" });
    head.appendChild(el("b", { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      (t.finished ? (failed ? "⚠️ " : "✅ ") : "📤 ") + t.label));
    const x = el("button", { border: "none", background: "none", cursor: "pointer", color: "#888", padding: "0 2px" }, "✕");
    x.dataset.hide = String(t.id);
    x.title = t.finished ? "닫기" : "표시만 닫기 (업로드는 계속됩니다)";
    head.appendChild(x);
    row.appendChild(head);

    const bar = el("div", { height: "6px", background: "#e9eef5", borderRadius: "3px", overflow: "hidden", margin: "4px 0" });
    bar.appendChild(el("div", { height: "100%", width: `${Math.round((done / n) * 100)}%`, background: failed ? "#e39b3b" : "#3b82f6", transition: "width .3s" }));
    row.appendChild(bar);

    const status = t.finished
      ? `완료 ${done}/${n}` + (failed ? ` · 실패 ${failed}` : "")
      : `전송 ${sent}/${n} · 드라이브 저장 ${done}/${n}` + (failed ? ` · 실패 ${failed}` : "");
    row.appendChild(el("div", { color: "#555" }, status));

    if (!t.finished && sent < n) {
      row.appendChild(el("div", { color: "#b45309" }, "전송 중 — 이 탭은 닫지 마세요 (매물창은 닫아도 됩니다)"));
    } else if (!t.finished) {
      row.appendChild(el("div", { color: "#15803d" }, "서버 전달 완료 — 창을 닫아도 계속 저장됩니다"));
    }
    if (t.limitMsg) row.appendChild(el("div", { color: "#d00" }, t.limitMsg));
    const firstErr = t.items.find((i) => i.st === "failed" && i.err);
    if (firstErr) row.appendChild(el("div", { color: "#d00", wordBreak: "break-all" }, `오류: ${firstErr.err}`));

    box.appendChild(row);
  }
}

// ---------------------------------------------------------------------
// 서버 전달 전 사진이 남아 있을 때만 탭 닫기 경고
// ---------------------------------------------------------------------
if (typeof window !== "undefined" && !window.__photoQueueUnloadHook) {
  window.__photoQueueUnloadHook = true;
  window.addEventListener("beforeunload", (e) => {
    const unsent = tasks.some((t) => t.items.some((i) => i.st === "waiting" || i.st === "sending"));
    if (unsent) { e.preventDefault(); e.returnValue = ""; }
  });
}
